import React, { useCallback, useState } from 'react';
import { useAuth, usePlatformBrowseMode } from '@/contexts';
import ShareModal from '@/components/ShareModal/ShareModal';
import { openMobileEditorShareNativeFirstOrModal } from '@/utils/editorMobileShare';
import { getShareContentType, type ShareContentType, type ShareModalEntry } from '@/lib/analyticsEvents';

interface ShareModalState {
  isOpen: boolean;
  postId: string | null;
  postName: string;
  content_type?: ShareContentType;
  prefetchedShareUrl?: string;
}

const CLOSED_STATE: ShareModalState = {
  isOpen: false,
  postId: null,
  postName: '',
};

/**
 * Shared post-share behavior: native share sheet first on mobile (via Capacitor
 * or the Web Share API), with a full fallback to the in-app ShareModal. Returns
 * a stable `handleShareClick(postId, postName, postType?)` plus a rendered
 * `shareModal` node to place in the surface's tree.
 */
export function usePostShare(opts?: {
  entry?: ShareModalEntry;
  onShareModalOpen?: () => void;
  onShareModalClose?: () => void;
}) {
  const entry = opts?.entry ?? 'share_icon';
  const onShareModalOpen = opts?.onShareModalOpen;
  const onShareModalClose = opts?.onShareModalClose;
  const { user } = useAuth();
  const { isOpenBrowse } = usePlatformBrowseMode();
  const [shareModalState, setShareModalState] = useState<ShareModalState>(CLOSED_STATE);

  const handleShareClick = useCallback(
    async (postId: string, postName: string, postType?: string) => {
      if (!user && !isOpenBrowse) return;

      const content_type = getShareContentType(postType) || 'list';
      await openMobileEditorShareNativeFirstOrModal({
        postId,
        contentType: content_type,
        entry,
        guestOpenBrowseFallback: !user && isOpenBrowse,
        openShareModal: (shareOpts) => {
          onShareModalOpen?.();
          setShareModalState({
            isOpen: true,
            postId,
            postName,
            content_type,
            prefetchedShareUrl: shareOpts?.prefetchedShareUrl,
          });
        },
      });
    },
    [user, isOpenBrowse, entry, onShareModalOpen],
  );

  const closeShareModal = useCallback(() => {
    setShareModalState(CLOSED_STATE);
    onShareModalClose?.();
  }, [onShareModalClose]);

  const shareModal =
    shareModalState.isOpen && shareModalState.postId ? (
      <ShareModal
        isOpen
        onClose={closeShareModal}
        resourceType="post"
        resourceId={shareModalState.postId}
        title={shareModalState.postName}
        content_type={shareModalState.content_type}
        url={shareModalState.prefetchedShareUrl}
        entry={entry}
      />
    ) : null;

  return { handleShareClick, shareModal };
}
